import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {PaymentInformation, Policy, Requirement, User} from 'lib/entities';

export type RecipientViewRequestBody = {
    envelopeId: string;
    customerId: string;
    returnUrl: string;
};

export type RecipientViewResponseBody = {
    recipientViewUrl: string;
};

export const api = createApi({
    baseQuery: fetchBaseQuery({baseUrl: '/api/'}),
    tagTypes: ['Requirement', 'PaymentInformation', 'Policy', 'CurrentUser'],
    endpoints: (build) => ({
        getRequirements: build.query<Requirement[], void>({
            // TODO: using separate requirements until camel vs snakecase
            // response props are fixed
            query: () => 'requirements/',
            transformResponse: (resp: Requirement[]) => {
                return resp.map((r) => ({
                    ...r,
                    dataJson: JSON.parse(r.dataJson || '{}'),
                }));
            },
            providesTags: (result) =>
                result ? [...result.map(({id}) => ({type: 'Requirement' as const, id}))] : ['Requirement'],
        }),
        updateRequirement: build.mutation<Requirement, Partial<Requirement>>({
            query: (body) => ({
                url: 'updateRequirement/',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: 'Requirement' as const, id: arg.id},
                'Policy',
                'CurrentUser',
            ],
        }),
        updatePaymentInformationRequirement: build.mutation<
            PaymentInformation,
            Partial<PaymentInformation & {requirementId: string}>
        >({
            query: ({id, ...body}) => ({
                url: 'updatePaymentInformationRequirement/',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['Requirement', 'CurrentUser'],
        }),
        getCurrentUser: build.query<User, void>({
            query: () => 'getCurrentUser/',
            providesTags: ['CurrentUser'],
        }),
        upsertAddressPaperwork: build.mutation<any, any>({
            query: (body) => ({
                url: 'address/',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CurrentUser'],
        }),
        submitApplication: build.mutation({
            query: () => ({
                url: 'submitApplication/',
                method: 'POST',
            }),
            invalidatesTags: ['CurrentUser'],
        }),
        upsertBirthStatusPaperwork: build.mutation<any, any>({
            query: (body) => ({
                url: 'birthStatus/',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useGetCurrentUserQuery,
    useGetRequirementsQuery,
    useUpdatePaymentInformationRequirementMutation,
    useUpdateRequirementMutation,
    useUpsertAddressPaperworkMutation,
    useUpsertBirthStatusPaperworkMutation,
    useSubmitApplicationMutation,
} = api;

export default api;
